import React, { CSSProperties, ReactNode } from "react";
import styled from "styled-components";
import Panel from "common/NewLook/Panel.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

export interface NewLookDetailsItem {
  key: string;
  label: ReactNode;
  value: ReactNode;
}

interface NewLookDetailsGridProps {
  title?: ReactNode;
  items: NewLookDetailsItem[];
  columns?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Key figures of a drawer as one panel: the panel's title row and a grid of
 * label / value cells separated by hairlines (replaces loose label lists).
 */
const NewLookDetailsGrid = ({
  title,
  items,
  columns = 2,
  className,
  style,
}: NewLookDetailsGridProps) => (
  <div style={style}>
    <ClippedPanel title={title} flush={true} className={className}>
      <Grid $columns={columns}>
        {items.map((item) => (
          <Cell key={item.key} $columns={columns}>
            <Label>{item.label}</Label>
            <Value>{item.value ?? "N/A"}</Value>
          </Cell>
        ))}
      </Grid>
    </ClippedPanel>
  </div>
);

export default NewLookDetailsGrid;

const ClippedPanel = styled(Panel)`
  overflow: hidden;
`;
const Grid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$columns}, minmax(0, 1fr));
  /* Each cell draws its right and bottom hairline; the outer ones are
     clipped by the panel so lines only show between cells. */
  margin: 0 -1px -1px 0;
  @media (max-width: 420px) {
    grid-template-columns: repeat(
      ${(p) => Math.min(p.$columns, 2)},
      minmax(0, 1fr)
    );
  }
`;
const lastOddSpansRow = `
  &:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }
`;
const Cell = styled.div<{ $columns: number }>`
  min-width: 0;
  padding: 12px 20px;
  box-shadow: 1px 0 0 ${(p) => getNewLookTokens(p.theme).hairline},
    0 1px 0 ${(p) => getNewLookTokens(p.theme).hairline};
  ${(p) => p.$columns === 2 && lastOddSpansRow}
  @media (max-width: 420px) {
    padding: 12px 16px;
    ${(p) => p.$columns === 3 && lastOddSpansRow}
  }
`;
const Label = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Value = styled.div`
  margin-top: 4px;
  ${newLookType.body};
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  min-width: 0;
  overflow-wrap: anywhere;
`;
